import { Box, Skeleton, Text, VStack } from '@chakra-ui/react';

import { AreaChart, type AreaData, FormatType } from '@revelio/replots';

import { calcMarkerPercent } from './chart-utils';
import { PercentileBar } from './PercentileBar';

/** Revelio brand color for the salary marker line */
const REVELIO_GREEN = '#2D426A';

export const SalaryChartContent = ({
  isLoading,
  error,
  areaData,
  userSalary,
  rawUserSalary,
  percentile,
  salaryFormatter,
  currencyCode,
  salaryTypeLabel,
  chartColors,
}: {
  isLoading: boolean;
  error: string | null;
  areaData: AreaData[];
  /** Currency-converted salary for chart marker positioning */
  userSalary: number;
  /** Raw USD salary for display labels (salaryFormatter already applies exchange rate) */
  rawUserSalary: number;
  percentile: number | null;
  salaryFormatter: (value: number) => string;
  currencyCode: string;
  salaryTypeLabel: string;
  chartColors: string[];
}) => {
  if (isLoading) {
    return <Skeleton height="300px" borderRadius="md" />;
  }

  if (error) {
    return (
      <Box bg="orange.50" p={4} borderRadius="md" textAlign="center">
        <Text color="orange.600" fontSize="sm">
          {error}
        </Text>
      </Box>
    );
  }

  if (areaData.length > 0 && areaData[0]?.values.length) {
    const markerPct = calcMarkerPercent(userSalary, areaData);

    return (
      <VStack align="stretch" spacing={4}>
        <Box position="relative">
          <Box height="300px">
            <AreaChart
              data={areaData}
              format={FormatType.CURRENCY}
              currencyCode={currencyCode}
              showYAxis={false}
              colors={chartColors}
            />
          </Box>
          {userSalary > 0 && markerPct !== null && percentile !== null && (
            <Box
              position="absolute"
              top={0}
              bottom="12px"
              left={`${markerPct}%`}
              width="2px"
              bg={REVELIO_GREEN}
              zIndex={2}
              pointerEvents="none"
            >
              <Box
                position="absolute"
                top="-24px"
                left="50%"
                transform="translateX(-50%)"
                bg={REVELIO_GREEN}
                color="white"
                px={2}
                py={0.5}
                borderRadius="sm"
                fontSize="xs"
                fontWeight="bold"
                whiteSpace="nowrap"
              >
                Your {salaryTypeLabel}: {salaryFormatter(rawUserSalary)}
              </Box>
            </Box>
          )}
        </Box>

        {percentile !== null && <PercentileBar percentile={percentile} />}
      </VStack>
    );
  }

  return (
    <Box bg="gray.50" p={8} borderRadius="md" textAlign="center">
      <Text color="gray.500" fontSize="sm">
        No salary distribution data available for this combination. Try
        adjusting the filters.
      </Text>
    </Box>
  );
};
